import { useCallback, useContext, useEffect, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ParagraphText } from "../../../../../Libraries";
import ReportPageHeader from "../ReportPageHeader";
import NonIdealNew from "../../../../common/NonIdealNew";
import { FilterNonIdeal } from "../../../../common";
import styles from "../styles.module.css";
import { useReports } from "../../../../../hooks/useReports";
import { useAuthContext } from "../../../../../context/AuthContext";
import { formatReportCurrency, getStartAndEndDate, REPORT_DATE_FILTERS } from "../../../../../utils";
import dayjs from "dayjs";
import { LanguageContext } from "../../../../../context/LanguageContext";
import useReportExport from "../../../../../hooks/useReportExport";
import useReportFilter from "../../../../../hooks/useReportFilter";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";

const SKELETON_ROWS = [
  { labelWidth: 95,  valueWidth: 88  },
  { labelWidth: 158, valueWidth: 112 },
  { labelWidth: 105, valueWidth: 96  },
  { labelWidth: 78,  valueWidth: 80  },
  { labelWidth: 90,  valueWidth: 104 },
];

const SaleSummary = () => {
  const [I18n] = useContext(LanguageContext);
  const { handleFetchReport, loading, salesSummary } = useReports(I18n);
  const { business } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchReport = useCallback((start, end) => {
    handleFetchReport({
      business_id: business?.id,
      report_type: "sale_summary",
      start_date: dayjs(start?.toDate ? start.toDate() : start).toISOString(),
      end_date: dayjs(end?.toDate ? end.toDate() : end).toISOString(),
    });
  }, [handleFetchReport, business?.id]);


  useEffect(() => {
    triggerLogEvent(analytics, "report_sale_summary_visited", {});
  }, []);

  const { handleExport, exportLoading } = useReportExport("sale_summary");
  const { headerDefaultFilter, isYearlyFilter, resetCount, handleFilterDate, handleResetFilter } = useReportFilter(fetchReport, "sale_summary");

  const hasNoData = !loading && salesSummary.every(({ value }) => !value);
  const showNonIdeal = isYearlyFilter && hasNoData;

  const minDate = useMemo(() => dayjs().subtract(1, "year"), []);
  const maxDate = useMemo(() => dayjs().endOf("day"), []);

  return (
    <Box id="sale_summary_page_wrapper" className={styles.pageWrapper}>
      <ReportPageHeader
        title={I18n.sales_summary}
        id="sale_summary"
        dateFilters={REPORT_DATE_FILTERS}
        onFilterDate={handleFilterDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultFilter={headerDefaultFilter}
        resetKey={resetCount}
        hideActions={showNonIdeal}
        onExport={handleExport}
        exportLoading={exportLoading}
      />
      <Box id="sale_summary_page_body" className={styles.pageBody}>
        {showNonIdeal ? (
          <NonIdealNew
            buttonOneId="sale_summary_nonideal_start_sale_btn"
            title={I18n.no_data_yet}
            subtitle={I18n.it_looks_like_you_dont_have_any_data_yet}
            button1Title={I18n.start_new_sale}
            onButton1Click={() => {
              triggerLogEvent(analytics, "report_nonideal_start_new_sale", { report_type: "sale_summary" });
              navigate("/QuickSale/QuickSaleForm", { state: { pathname: location.pathname } });
            }}
            loading={false}
            disabledOne={false}
          />
        ) : loading ? (
          SKELETON_ROWS.map(({ labelWidth, valueWidth }, i) => (
            <Box key={i} className={styles.summaryRow}>
              <Skeleton variant="rounded" animation="wave" width={labelWidth} height={18} sx={{ borderRadius: "6px" }} />
              <Skeleton variant="rounded" animation="wave" width={valueWidth} height={18} sx={{ borderRadius: "6px" }} />
            </Box>
          ))
        ) : !isYearlyFilter && hasNoData ? (
          <FilterNonIdeal
            title={I18n.no_transactions_matching_those_filters}
            subTitle={`${I18n.try_adjusting_filters} `}
            subTitleText={I18n.view_yearly_data}
            subTitleTextOnClick={handleResetFilter}
            subTitleTextId="sale_summary_filter_view_yearly"
          />
        ) : (
          salesSummary.map(({ label, value }, i) => (
            <Box key={label} id={`sale_summary_row_${i}`} className={styles.summaryRow}>
              <ParagraphText id={`sale_summary_label_${i}`} className={styles.summaryLabel}>{label}</ParagraphText>
              <ParagraphText id={`sale_summary_value_${i}`} className={styles.summaryValue}>{formatReportCurrency(value)}</ParagraphText>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default SaleSummary;
